import { motion } from 'framer-motion';
import { Brain, Wallet, Rocket } from 'lucide-react';

const GROUPS = [
  {
    key: 'decide',
    label: 'Decide',
    icon: Brain,
    tint: 'glass-cyan',
    accent: 'text-cyan-300',
    lead: 'What actually fits, and what is missing.',
    items: [
      ['Career Fit', 'Interests, traits, values, environment and lifestyle, scored, with the reasoning shown'],
      ['Skills Intelligence', 'What you can already do, including military roles, athletics and volunteer work'],
      ['College Intelligence Kit', 'Four questions kept separate: do I want it, can I get in, can I afford it, is it worth it'],
      ['Transition Intelligence', 'Here to there: transferable skills, match, gaps, the bridge, the timeline'],
      ['Future-of-Work', 'Growth, wages, demand and honest AI exposure for the path you are choosing'],
    ],
  },
  {
    key: 'afford',
    label: 'Afford',
    icon: Wallet,
    tint: 'glass-emerald',
    accent: 'text-emerald-300',
    lead: 'What you can realistically do next.',
    items: [
      ['Lifestyle Design', 'The cost of the life you want, and the salary that funds it'],
      ['Learning ROI', 'Cost, months, borrowing, wage lift and payback on any credential'],
      ['Funding & Grants', 'Scholarships, workforce funding, benefits and the new Workforce Pell for short-term training'],
      ['Financial Intelligence', 'Exposure, savings, the shortfall and current federal borrowing rules'],
      ['Financial Literacy', 'The money mechanics nobody taught you, in the moment they matter'],
    ],
  },
  {
    key: 'launch',
    label: 'Launch',
    icon: Rocket,
    tint: 'glass-violet',
    accent: 'text-violet-300',
    lead: 'Getting hired, and staying ahead.',
    items: [
      ['Career Launch Readiness', 'One number against your target, across six things employers weigh'],
      ['Brand & Visibility', 'How strangers decide whether to reply to you'],
      ['Mentors & Network', 'Live, web-sourced people and organisations, not a stale directory'],
      ['Proof of Skill', 'Evidence behind every claim, pulled from your own documents'],
      ['Paths to My Future Self', 'The same person, modelled forward under different decisions'],
      ['Opportunity Radar', 'What is moving in your field, and what to do about it'],
    ],
  },
];

export default function IntelligenceLayer() {
  return (
    <section id="intelligence" className="relative py-24 md:py-32 px-6 md:px-12" data-testid="intelligence-layer">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-3xl">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-300">The intelligence layer</span>
          <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mt-4">
            Decision intelligence, not content
          </h2>
          <p className="text-lg text-slate-300/90 mt-5 leading-relaxed">
            A quiz says "become a cybersecurity analyst". The engine says: you already hold 68% of the required
            skills, these four are missing, this credential closes the gap, this workforce grant may pay for it,
            these employers are hiring near you, four to eight months, and here is the button that builds the plan.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mt-12">
          {GROUPS.map((g, gi) => (
            <motion.div key={g.key} initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: gi * 0.1 }}
              className={`glass-tint ${g.tint} p-7`} data-testid={`intel-group-${g.key}`}>
              <g.icon className={`w-7 h-7 ${g.accent}`} />
              <h3 className="font-outfit text-2xl font-semibold text-white mt-4">{g.label}</h3>
              <p className="text-sm text-slate-300/80 mt-1.5">{g.lead}</p>
              <div className="mt-6 space-y-4">
                {g.items.map(([title, body]) => (
                  <div key={title} className="border-l-2 border-white/15 pl-4">
                    <p className="text-sm font-semibold text-white">{title}</p>
                    <p className="text-sm text-slate-300/75 mt-0.5 leading-relaxed">{body}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        <p className="text-sm text-slate-300 mt-10 max-w-3xl">
          Which engines switch on is decided by what is happening in your life, and it changes as you do.
          Patricia, our guide, explains any of it out loud, in a real voice, whenever you want it.
        </p>
      </div>
    </section>
  );
}
